import { Box, Chip, Typography } from '@material-ui/core';
import React, { PropsWithChildren, useRef, useState } from 'react';

import Android12Switch from '../../../../components/Android12Switch';

export type Option<T> = {
  label: string;
  value: T;
};

export interface MultipleSelectionProps<T> {
  title?: string;
  options: Option<T>[];
  defaultValue?: T[];
  toggleOn?: boolean;
  onChange?: (options: T[]) => void;
  onToggleChange?: (toggleOn: boolean) => void;
}

const MultipleSelection = <T,>({
  title,
  defaultValue,
  options,
  toggleOn = false,
  onChange,
  onToggleChange
}: PropsWithChildren<MultipleSelectionProps<T>>): JSX.Element => {
  const [value, setValue] = useState<T[]>([...(defaultValue || [])]);

  const switchRef = useRef<HTMLInputElement>();
  const switchDefaultChecked = useRef(toggleOn);

  const handleToggle = (
    event: React.MouseEvent<HTMLDivElement, MouseEvent>,
    optionValue: T
  ) => {
    event.stopPropagation();

    const index = value.indexOf(optionValue);

    if (index > -1) {
      value.splice(index, 1);
    } else {
      value.push(optionValue);
    }

    setValue([...value]);
    onChange && onChange(value);
  };

  const handleToggleChange = (
    event: React.ChangeEvent<HTMLInputElement> | null,
    checked: boolean
  ) => {
    if (!onToggleChange) return;

    onToggleChange(checked);
  };

  const handleClickInside = () => {
    if (!toggleOn && switchRef.current) {
      switchRef.current.click();
      handleToggleChange(null, true);
    }
  };

  return (
    <Box onClick={handleClickInside}>
      <Box display="flex" alignItems="center">
        {title && <Typography variant="h4">{title}</Typography>}

        <Box flex="1" display="flex" justifyContent="flex-end">
          <Android12Switch
            inputRef={switchRef}
            defaultChecked={switchDefaultChecked.current}
            edge="end"
            color="primary"
            onClick={(event) => event.stopPropagation()}
            onChange={handleToggleChange}
          />
        </Box>
      </Box>

      <Box mt={1} display="flex" flexWrap="wrap">
        {options.map((option) => (
          <Box key={option.label} mt={1} mr={1}>
            <Chip
              clickable
              disabled={!toggleOn}
              label={option.label}
              onClick={(event) => handleToggle(event, option.value)}
              color={
                toggleOn && value.includes(option.value)
                  ? 'secondary'
                  : 'default'
              }
            />
          </Box>
        ))}
      </Box>
    </Box>
  );
};

export default MultipleSelection;
